import type { ScrollSmoother } from 'gsap/ScrollSmoother';

export function setupHomeNavigation(
  root: HTMLElement,
  smoother: ScrollSmoother | null
) {
  const cleanups: Array<() => void> = [];
  const drawer = root.querySelector<HTMLElement>('.at-offcanvas-2-area');
  const menuTriggers = Array.from(
    root.querySelectorAll<HTMLElement>('.hamburger-open-btn')
  );
  const menuClosers = Array.from(
    root.querySelectorAll<HTMLElement>('.hamburger-close-btn')
  );
  const bodyOverlay = root.querySelector<HTMLElement>('.body-overlay');
  const searchPanel = root.querySelector<HTMLElement>('.at-search-form-toggle');
  const searchOverlay = root.querySelector<HTMLElement>(
    '.at-search-body-overlay'
  );
  const restoredLinks = new Map<HTMLElement, string>();
  let menuCloseTimer: ReturnType<typeof setTimeout> | undefined;

  root
    .querySelectorAll<HTMLElement>('.at-main-menu > nav > ul > li > a')
    .forEach((link) => {
      if (link.querySelector('.at-link-swap')) return;
      const textNode = Array.from(link.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
      );
      const label = textNode?.textContent?.trim();
      if (!textNode || !label) return;
      restoredLinks.set(link, link.innerHTML);
      const swap = document.createElement('span');
      swap.className = 'at-link-swap';
      const first = document.createElement('span');
      first.className = 'text-1';
      first.textContent = label;
      const second = document.createElement('span');
      second.className = 'text-2';
      second.textContent = label;
      swap.append(first, second);
      textNode.replaceWith(swap);
    });

  const listen = <K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    event: K,
    handler: (event: HTMLElementEventMap[K]) => void
  ) => {
    target.addEventListener(event, handler);
    cleanups.push(() => target.removeEventListener(event, handler));
  };

  const setMenuState = (open: boolean) => {
    if (menuCloseTimer) clearTimeout(menuCloseTimer);
    drawer?.classList.toggle('menu-open', open);
    drawer?.classList.remove('menu-open-temp');
    if (!open && drawer) {
      drawer.classList.add('menu-open-temp');
      menuCloseTimer = setTimeout(() => {
        drawer.classList.remove('menu-open-temp');
      }, 650);
    }
    drawer?.setAttribute('aria-hidden', String(!open));
    menuTriggers.forEach((trigger) => {
      trigger.classList.toggle('active', open);
      trigger.setAttribute('aria-expanded', String(open));
    });
    menuClosers.forEach((closer) => closer.classList.toggle('active', open));
    // The source design dims the page behind the drawer through `.body-overlay.apply`
    // (rgba(24, 24, 24, .5)); the stylesheet ships it, only the class was never applied.
    bodyOverlay?.classList.toggle('apply', open);
    document.body.classList.toggle('mobile-menu-is-open', open);
  };

  if (drawer) {
    drawer.id = 'shipSmartMobileMenu';
    drawer.setAttribute('aria-hidden', 'true');
  }
  menuTriggers.forEach((trigger) => {
    trigger.setAttribute('aria-controls', 'shipSmartMobileMenu');
    trigger.setAttribute('aria-expanded', 'false');
    listen(trigger, 'click', () => setMenuState(true));
  });
  menuClosers.forEach((closer) =>
    listen(closer, 'click', () => setMenuState(false))
  );

  const offcanvasBackground = root.querySelector<HTMLElement>('.offcanvas-bg');
  if (offcanvasBackground) {
    listen(offcanvasBackground, 'click', (event) => {
      if (event.target === offcanvasBackground) setMenuState(false);
    });
  }
  if (bodyOverlay) {
    listen(bodyOverlay, 'click', () => setMenuState(false));
  }

  root
    .querySelectorAll<HTMLElement>('.at-offcanvas-menu a:not([href="#"])')
    .forEach((link) => listen(link, 'click', () => setMenuState(false)));

  /** Drawer submenu accordion. Parent labels toggle their submenu and never navigate. */
  const openedSubmenus = new Set<HTMLElement>();

  const slideOpen = (submenu: HTMLElement) => {
    openedSubmenus.add(submenu);
    submenu.style.display = 'block';
    submenu.style.overflow = 'hidden';
    submenu.style.height = '0px';
    submenu.style.transition = 'height .3s ease';
    const target = submenu.scrollHeight;
    window.requestAnimationFrame(() => {
      submenu.style.height = `${target}px`;
    });
    let completionTimer: number | undefined;
    const done = () => {
      if (completionTimer !== undefined) {
        window.clearTimeout(completionTimer);
        completionTimer = undefined;
      }
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
      submenu.removeEventListener('transitionend', done);
    };
    submenu.addEventListener('transitionend', done);
    completionTimer = window.setTimeout(done, 350);
  };

  const slideClosed = (submenu: HTMLElement) => {
    submenu.style.overflow = 'hidden';
    submenu.style.height = `${submenu.scrollHeight}px`;
    submenu.style.transition = 'height .3s ease';
    window.requestAnimationFrame(() => {
      submenu.style.height = '0px';
    });
    let completionTimer: number | undefined;
    const done = () => {
      if (completionTimer !== undefined) {
        window.clearTimeout(completionTimer);
        completionTimer = undefined;
      }
      submenu.style.display = '';
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
      submenu.removeEventListener('transitionend', done);
      openedSubmenus.delete(submenu);
    };
    submenu.addEventListener('transitionend', done);
    completionTimer = window.setTimeout(done, 350);
  };

  root
    .querySelectorAll<HTMLElement>('.at-offcanvas-menu li.has-dropdown')
    .forEach((item) => {
      const submenu = item.querySelector<HTMLElement>(':scope > ul');
      const trigger = item.querySelector<HTMLElement>(
        ':scope > .at-mobile-menu-parent-trigger'
      );
      if (!submenu || !trigger) return;

      trigger.setAttribute('aria-controls', submenu.id);

      const onToggle = (event: Event) => {
        event.preventDefault();
        const open = !item.classList.contains('active');
        item.classList.toggle('active', open);
        trigger.setAttribute('aria-expanded', String(open));
        if (open) slideOpen(submenu);
        else slideClosed(submenu);
      };

      listen(trigger, 'click', onToggle);
    });

  cleanups.push(() => {
    openedSubmenus.forEach((submenu) => {
      submenu.style.display = '';
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
    });
    root
      .querySelectorAll<HTMLElement>('.at-offcanvas-menu li.has-dropdown')
      .forEach((item) => {
        item.classList.remove('active');
        item
          .querySelector<HTMLElement>(':scope > .at-mobile-menu-parent-trigger')
          ?.setAttribute('aria-expanded', 'false');
      });
  });

  const closeSearch = () => {
    searchPanel?.classList.remove('active');
    searchOverlay?.classList.remove('active');
  };
  root.querySelectorAll<HTMLElement>('.at-search-click').forEach((trigger) =>
    listen(trigger, 'click', () => {
      searchPanel?.classList.add('active');
      searchOverlay?.classList.add('active');
    })
  );
  root
    .querySelectorAll<HTMLElement>('.at-search-close, .at-search-body-overlay')
    .forEach((target) => listen(target, 'click', closeSearch));

  const keydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (drawer?.classList.contains('menu-open')) setMenuState(false);
    closeSearch();
  };
  document.addEventListener('keydown', keydown);
  cleanups.push(() => document.removeEventListener('keydown', keydown));

  const backToTop = root.querySelector<HTMLElement>('#back_to_top');
  const backToTopWrapper = root.querySelector<HTMLElement>(
    '.back-to-top-wrapper'
  );
  const stickyMenu = root.querySelector<HTMLElement>('#header-sticky');
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const scrolledPastHeader = scrollTop >= 120;
    backToTopWrapper?.classList.toggle('back-to-top-btn-show', scrollTop > 300);
    stickyMenu?.classList.toggle('header-sticky', scrolledPastHeader);
  };
  window.addEventListener('scroll', handleScroll, { passive: true });
  cleanups.push(() => window.removeEventListener('scroll', handleScroll));
  handleScroll();

  if (backToTop) {
    listen(backToTop, 'click', () => {
      if (smoother) smoother.scrollTo(0, false);
      else window.scrollTo({ top: 0, behavior: 'smooth' });
      backToTopWrapper?.classList.remove('back-to-top-btn-show');
      stickyMenu?.classList.remove('header-sticky');
      window.requestAnimationFrame(handleScroll);
    });
  }

  return () => {
    cleanups.forEach((cleanup) => cleanup());
    if (menuCloseTimer) clearTimeout(menuCloseTimer);
    drawer?.classList.remove('menu-open', 'menu-open-temp');
    menuTriggers.forEach((trigger) => {
      trigger.classList.remove('active');
      trigger.setAttribute('aria-expanded', 'false');
    });
    menuClosers.forEach((closer) => closer.classList.remove('active'));
    bodyOverlay?.classList.remove('apply');
    document.body.classList.remove('mobile-menu-is-open');
    closeSearch();
    restoredLinks.forEach((markup, link) => {
      link.innerHTML = markup;
    });
  };
}
